import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { NotificationSetting } from '../models/CompanyOnboardingModels';
import { Device, Sim, SimStatus, UpgradeDeviceRequest } from '../models/UserDevicesModels';

interface UpgradeDeviceStateData {
  upgradeDeviceRequest: UpgradeDeviceRequest;
  newDevice: Device | null;
  deviceToUpgrade: Device | null;
  simStatus: SimStatus;
  typeOfConnection: '' | 'ESIM' | 'DEVICE';
  selectedSimId: string;
  selectedSimType: '' | 'ESIM' | 'PSIM';
  qrCodeNotificationSettings: NotificationSetting[];
  hasDistributionList: boolean;
  transferEnabled: boolean; // bool from BE that says if feature is enabled or not
  transferPullEnabled: boolean;
  transferEligible: boolean; // bool so we know if current sim swap can be a transfer
}

const initialState: UpgradeDeviceStateData = {
  upgradeDeviceRequest: {
    notifyAdmin: true,
    journey: 'UPGRADE',
    eid: '',
    userId: '',
    notificationType: '',
    phoneNumber: '',
    qrCodeNotification: 'ADMIN',
    notifyDistributionList: false,
    transfer: false,
  },
  simStatus: SimStatus.CONNECTED,
  newDevice: null,
  deviceToUpgrade: null,
  typeOfConnection: 'DEVICE',
  selectedSimId: '',
  selectedSimType: '',
  qrCodeNotificationSettings: [],
  hasDistributionList: false,
  transferEnabled: false,
  transferPullEnabled: true,
  transferEligible: false,
};

export const UpgradeDeviceSlice = createSlice({
  name: 'upgradeDevice',
  initialState,
  reducers: {
    setNewDevice: (state, action: PayloadAction<Device>) => {
      state.newDevice = action.payload;
    },
    setDeviceToUpgrade: (state, action: PayloadAction<{ device: Device; sim: Sim }>) => {
      state.deviceToUpgrade = action.payload.device;
      state.selectedSimId = action.payload.sim.simId;
      state.selectedSimType = action.payload.sim.type as 'PSIM' | 'ESIM' | '';
      state.simStatus = action.payload.sim.status;
      state.upgradeDeviceRequest = { ...state.upgradeDeviceRequest, phoneNumber: action.payload.sim.phoneNumber };
    },
    updateTypeOfConnection: (state, action: PayloadAction<'' | 'ESIM' | 'DEVICE'>) => {
      state.upgradeDeviceRequest.eid = '';
      state.typeOfConnection = action.payload;
    },
    updateRequest: (state, action: PayloadAction<Partial<UpgradeDeviceRequest>>) => {
      state.upgradeDeviceRequest = {
        ...state.upgradeDeviceRequest,
        ...action.payload,
      };
    },
    setNotificationSettings: (state, action: PayloadAction<NotificationSetting[]>) => {
      state.hasDistributionList = action.payload.some((setting) => setting.type === 'DISTRIBUTION_LIST');
      if (action.payload.some((setting) => setting.type === 'END_USER'))
        state.upgradeDeviceRequest.qrCodeNotification = 'END_USER';
      state.qrCodeNotificationSettings = action.payload;
    },
    setTransferEnabled: (state, action: PayloadAction<boolean>) => {
      state.transferEnabled = action.payload;
    },
    setTransferPullEnabled: (state, action: PayloadAction<boolean>) => {
      state.transferPullEnabled = action.payload;
    },
    setTransferEligible: (state, action: PayloadAction<boolean>) => {
      if (!state.transferEnabled) {
        state.transferEligible = false;
        return;
      }

      state.transferEligible =
        state.selectedSimType !== 'PSIM' && state.simStatus !== SimStatus.SUSPENDED ? action.payload : false;
    },
    reset: () => initialState,
  },
});

export const {
  updateRequest,
  setNewDevice,
  setDeviceToUpgrade,
  reset,
  updateTypeOfConnection,
  setNotificationSettings,
  setTransferEnabled,
  setTransferPullEnabled,
  setTransferEligible,
} = UpgradeDeviceSlice.actions;

export default UpgradeDeviceSlice.reducer;
